import { useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import useLang from "../../../hooks/useLang";
import { useNavigate } from "react-router-dom";
import usePolicyChecker from "../../auth/usePolicyChecker";
import { userActions } from "../../../config/accessConfig";
import {
  selectConfigurations,
  addConfiguration,
  removeConfiguration,
} from "../configurationsSlice";
import Icon from "../../../components/Icon";
import modelImages from "../mockups/modelMockup";

const useConfigurationsList = () => {
  const lang = useLang();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tableRef = useRef();
  const canDelete = usePolicyChecker({
    action: userActions.SAVE_CONFIGURATION,
  });
  const configurations = useSelector(selectConfigurations);
  const defaultVariations = useSelector(
    (state) => state.configurations.defaultVariations,
  );

  // Enrich configurations with default variation image
  const dataRows = useMemo(() => {
    return configurations.map((config) => {
      if (config.image) return config;
      const defaultVarId = defaultVariations[config.id];
      if (!defaultVarId) return config;
      const img = modelImages[defaultVarId];
      if (!img) {
        // Fallback to 111-111
        const suffix = defaultVarId.replace(`${config.id}-`, "");
        const fallbackImg = modelImages[`111-111-${suffix}`];
        if (fallbackImg) return { ...config, image: fallbackImg };
      }
      if (img) return { ...config, image: img };
      return config;
    });
  }, [configurations, defaultVariations]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [creationOpen, setCreationOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState();
  const [newId, setNewId] = useState();
  const [newName, setNewName] = useState();

  const [widthChecked, setWidthChecked] = useState(false);
  const [heightChecked, setHeightChecked] = useState(false);
  const [colorChecked, setColorChecked] = useState(false);

  const handleRowClick = (data) => {
    navigate(data);
  };

  const handleCreate = () => {
    setCreationOpen(true);
  };

  const handleDelete = (data) => {
    setIdToDelete(data);
    setConfirmationOpen(true);
  };

  const handleClose = () => {
    setIdToDelete();
    setConfirmationOpen(false);
    setCreationOpen(false);
    setNewId();
    setNewName();
    setWidthChecked(false);
    setHeightChecked(false);
    setColorChecked(false);
  };

  const handleSubmit = () => {
    dispatch(removeConfiguration(idToDelete));
    setIdToDelete();
    setConfirmationOpen(false);
  };

  const handleCreateSubmit = () => {
    const newParameters = [];
    if (widthChecked) newParameters.push("Plotis");
    if (heightChecked) newParameters.push("Aukštis");
    if (colorChecked) newParameters.push("Spalva");
    dispatch(
      addConfiguration({
        id: newId,
        name: newName,
        image: null,
        parameters: newParameters,
      }),
    );
    setCreationOpen(false);
    setNewId();
    setNewName();
  };

  const handleCancel = () => {
    setIdToDelete();
    setConfirmationOpen(false);
    setCreationOpen(false);
    setNewId();
    setNewName();
    setWidthChecked(false);
    setHeightChecked(false);
    setColorChecked(false);
  };

  const tableConfig = [
    {
      id: "CONFIGURATION_ID",
      width: 44,
      header: { label: lang("configuration_id") },
      entityKey: "id",
    },
    {
      id: "IMAGE",
      width: 32,
      header: { label: lang("image") },
      entityKey: "image",
      component: (data) => {
        return (
          <div
            style={{
              width: "var(--width-20)",
              height: "var(--width-20)",
              borderRadius: "var(--rounded)",
              overflow: "hidden",
            }}
          >
            <img
              style={{ width: "100%", height: "100%" }}
              src={data || "image_placeholder.jpg"}
              alt="placeholder"
            />
          </div>
        );
      },
    },
    {
      id: "NAME",
      width: 44,
      header: { label: lang("name") },
      entityKey: "name",
    },
    {
      id: "PARAMETERS",
      width: 44,
      header: { label: lang("parameters") },
      entityKey: "parameters",
    },
    {
      id: "DELETE",
      width: 32,
      header: { label: null },
      entityKey: "delete",
      onClick: canDelete ? handleDelete : null,
      component: () => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-2)",
              color: canDelete
                ? "var(--clr-sky-700)"
                : "var(--clr-neutral-500)",
            }}
          >
            <Icon trashIcon emphasized={canDelete} />
            {lang("delete")}
          </div>
        );
      },
    },
  ];

  return {
    tableAttribs: {
      ref: tableRef,
      config: tableConfig,
      data: dataRows || [],
      onRowClick: handleRowClick,
    },
    handleCreate,
    disableChange: !canDelete,
    creationOpen,
    confirmationOpen,
    handleClose,
    handleSubmit,
    handleCreateSubmit,
    handleCancel,
    idToDelete,
    newId,
    setNewId,
    newName,
    setNewName,
    widthChecked,
    setWidthChecked,
    heightChecked,
    setHeightChecked,
    colorChecked,
    setColorChecked,
  };
};

export default useConfigurationsList;
